import { isFinite } from "lodash"
import moment from "moment"
import { memo, useEffect, useState } from "react"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import { unstable_batchedUpdates } from "react-dom"
import { useTranslation } from "react-i18next"
import api from "../../api"
import { useAppSelector } from "../../app/hooks"
import TrashIcon from "../../assets/trash.svg"
import { RootState } from "../../store"
import { countryNamesInEnglish, dateFormatter } from "../../utils/commonFunctions"

const LineHaulMAWBSTableExpandedRow = (props: any) => {
    const { t } = useTranslation()
    const { showPalletDetails } = props ?? {}
    const [loading, setLoading] = useState<boolean>(false)
    const [mawb, setMAWB] = useState<any>()
    const [error, setError] = useState<string>("")
    const { country: lhCountry, success } = useAppSelector((state: RootState) => state.linehaul)
    const pallets = mawb?.LHPallets?.length ? mawb.LHPallets : []
    const statusLogs = mawb?.LHMawbStatusLogs?.length ? mawb.LHMawbStatusLogs : []
    const attachments = mawb?.LHMawbDocs?.length ? mawb.LHMawbDocs : []

    useEffect(() => {
        const getMAWB = async (mawbId: string) => {
            try {
                setLoading(true)
                const response = (await api.linehaul.MAWBDetails({
                    mawb: props?.mawb?.mawb ?? ""
                })) as Record<string, any>
                if (!response?.data?.status || !response.data.payload)
                    throw new Error(response?.data?.message ?? "Unable to get MAW data")
                unstable_batchedUpdates(() => {
                    setMAWB(response.data.payload)
                    setError("")
                    setLoading(false)
                })
            } catch (e: any) {
                unstable_batchedUpdates(() => {
                    setMAWB(undefined)
                    setError(e.message ?? "Unable to get MAW data")
                    setLoading(false)
                })
            }
        }
        getMAWB(props.mawb.mawb_id)
    }, [props])

    if (loading)
        return (
            <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only"></span>
                </div>
            </div>
        )

    if (error) return <p className="text-danger text-center">{error}</p>

    return (
        <div className="container-fluid px-2" style={{ fontSize: "13px" }}>
            <div className="row">
                <div className="col-12 col-xl-7 mb-4 mb-xl-0">
                    <div>
                        {/* MAWB Details */}
                        <div className="row mb-3 fw-bold" style={{ fontSize: "16px" }}>
                            <div className="col-12">MAWB Details</div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-12 col-md-4 mb-2">
                                <div className="mb-1">MAWB #</div>
                                <div className="p-2 bg-white rounded text-truncate">{mawb?.mawb ?? "-"}</div>
                            </div>
                            <div className="col-12 col-md-4 mb-2">
                                <div className="mb-1">Flight Number </div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.flight_number ?? "-"}
                                </div>
                            </div>
                            <div className="col-12 col-md-4 mb-2">
                                <div className="mb-1">PRO Number</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.pro_number ?? "-"}
                                </div>
                            </div>
                            <div className="col-12 col-md-3 mb-2">
                                <div className="mb-1">Export Agent</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.export_agent ?? "-"}
                                </div>
                            </div>
                            <div className="col-12 col-md-3 mb-2">
                                <div className="mb-1">Import Agent</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.import_agent ?? "-"}
                                </div>
                            </div>
                            <div className="col-12 col-md-3 mb-2">
                                <div className="mb-1">Tracking Company</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.tracking_company ?? "-"}
                                </div>
                            </div>
                            <div className="col-12 col-md-3 mb-2">
                                <div className="mb-1">Carrier</div>
                                <div className="p-2 bg-white rounded text-truncate">{mawb?.carrier ?? "-"}</div>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                                <div className="mb-1">Origin</div>
                                <div className="p-2 bg-white rounded text-truncate">{mawb?.origin ?? "-"}</div>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                                <div className="mb-1">Destination</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.destination ?? "-"}
                                </div>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                                <div className="mb-1">Flight ETD</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {moment(mawb?.etd ?? "-").isValid()
                                        ? dateFormatter(mawb.etd, false, lhCountry)
                                        : "-"}
                                </div>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                                <div className="mb-1">Flight ETA</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {moment(mawb?.eta ?? "-").isValid()
                                        ? dateFormatter(mawb.eta, false, lhCountry)
                                        : "-"}
                                </div>
                            </div>
                            <div className="col-12 col-md-4 mb-2">
                                <div className="mb-1">Gross Weight (kg)</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.gross_weight ?? "-"}
                                </div>
                            </div>
                            <div className="col-12 col-md-4 mb-2">
                                <div className="mb-1">Chargeable Weight (kg)</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.chargeable_weight ?? "-"}
                                </div>
                            </div>
                            <div className="col-12 col-md-4 mb-2">
                                <div className="mb-1">No. of Packages</div>
                                <div className="p-2 bg-white rounded text-truncate">
                                    {mawb?.no_of_packages ?? "-"}
                                </div>
                            </div>
                            <div className="col-12">
                                <div className="border border-bottom"></div>
                            </div>
                        </div>
                        {/* Pallets */}
                        <div className="row mb-2 fw-bold" style={{ fontSize: "16px" }}>
                            <div className="col-12">Pallets</div>
                        </div>

                        {pallets.length ? (
                            <>
                                <div className="row mb-1">
                                    <div className="col-12">
                                        <div className="border">
                                            <div className="row">
                                                <div className="col-4">
                                                    <div className="p-2">PALLET ID</div>
                                                </div>
                                                <div className="col-3">
                                                    <div className="p-2">A.WEIGHT</div>
                                                </div>
                                                <div className="col-3">
                                                    <div className="p-2">C.WEIGHT</div>
                                                </div>
                                                <div className="col-2">
                                                    <div className="p-2">VALUE</div>
                                                </div>
                                                {/* <div className="col-2">
                                            <div className="p-2">
                                                High Value
                                            </div>
                                        </div> */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {pallets.map((pallet: any) => {
                                    const {
                                        pallet_id,
                                        actual_weight_value,
                                        ch_weight_value,
                                        weight_unit,
                                        pallet_invoice_value
                                    } = pallet
                                    return (
                                        <div key={pallet_id} className="row mb-2">
                                            <div className="col-12">
                                                <div className="bg-white">
                                                    <div className="row ">
                                                        <div className="col-4">
                                                            <div className="p-2 highlighted-text">
                                                                <span
                                                                    role="button"
                                                                    onClick={() => showPalletDetails?.(pallet)}>
                                                                    {pallet_id ?? "-"}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div className="col-3">
                                                            <div className="p-2">
                                                                {isFinite(actual_weight_value) && weight_unit
                                                                    ? `${actual_weight_value} ${weight_unit}`
                                                                    : "-"}
                                                            </div>
                                                        </div>
                                                        <div className="col-3">
                                                            <div className="p-2">
                                                                {isFinite(ch_weight_value) && weight_unit
                                                                    ? `${ch_weight_value} ${weight_unit}`
                                                                    : "-"}
                                                            </div>
                                                        </div>
                                                        <div className="col-2">
                                                            <div className="p-2">
                                                                {isFinite(pallet_invoice_value) ? (
                                                                    <>
                                                                        <span className="fw-bolder">
                                                                            {pallet_invoice_value}
                                                                        </span>
                                                                        &nbsp; &#36;
                                                                    </>
                                                                ) : (
                                                                    "-"
                                                                )}
                                                            </div>
                                                        </div>
                                                        {/* <div className="col-2">
                                                    <div className="p-2">
                                                        {type}
                                                    </div>
                                                </div> */}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </>
                        ) : (
                            <div className="row mb-3 text-muted">
                                <div className="col-12">No pallets...</div>
                            </div>
                        )}

                        {/* Comments */}
                        <div className="row" style={{ fontSize: "16px" }}>
                            <div className="col-12 mb-1">Comments</div>
                        </div>
                        <div className="row" style={{ maxWidth: "50vw" }}>
                            <div className="col-12 mb-2">
                                <div className="p-2 rounded bg-white text-muted">
                                    {mawb?.comments || "No comments"}
                                </div>
                            </div>
                            <div className="col-12 mb-3">
                                <div className="border border-bottom"></div>
                            </div>
                        </div>

                        {/* Document */}
                        <div className="row mb-3 fw-bold" style={{ fontSize: "16px" }}>
                            <div className="col-12">Documents</div>
                        </div>
                        {attachments.length ? (
                            <>
                                <div className="row mb-2">
                                    <div className="col-12">
                                        <div className="p-2 border rounded">
                                            <div className="row">
                                                <div className="col-2">TYPE</div>
                                                <div className="col-3">FILE NAME</div>
                                                <div className="col-3">DATE & TIME</div>
                                                <div className="col-3">DESCRIPTION</div>
                                                <div className="col-1"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {attachments.map((document: any) => {
                                    const { created_at } = document
                                    const createdAtMoment = moment(created_at ?? "")
                                    let dateAndTime = "-,-"
                                    dateAndTime = createdAtMoment.isValid()
                                        ? dateFormatter(created_at, true)
                                        : dateAndTime
                                    let date = dateAndTime.split(",")[0]?.trim()
                                    let time = dateAndTime.split(",")[1]?.trim()
                                    return (
                                        <div key={`${date}-${time}`} className="row mb-2">
                                            <div className="col-12">
                                                <div className="bg-white p-2">
                                                    <div className="row">
                                                        <div className="col-2 d-flex align-items-center text-truncate">
                                                            {document.document_name ?? "-"}
                                                        </div>
                                                        <div className="col-3 d-flex align-items-center text-truncate">
                                                            <a
                                                                rel="noreferrer"
                                                                href={document.file_path ?? "-"}
                                                                target="_blank"
                                                                className="text-primary text-truncate">
                                                                {document.file_name ?? "-"}
                                                            </a>
                                                        </div>
                                                        <div className="col-3 d-flex flex-column flex-nowrap justify-content-between">
                                                            <div>{date}</div>
                                                            <small className="text-muted">{time}</small>
                                                        </div>
                                                        <div className="col-3">{document.description}</div>
                                                        <div className="col-1">
                                                            <img
                                                                src={TrashIcon}
                                                                alt="delete"
                                                                onClick={() => props.deleteDocument?.(document)}
                                                                role="button"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </>
                        ) : (
                            <div className="row mb-4 text-muted">
                                <div className="col-12">No documents...</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Breaker */}
                <div className="col d-none d-xl-block border-end p-0 flex-shrink-1 flex-grow-0"></div>

                {/* Status History */}
                <div className="col">
                    <div className="row mb-3 fw-bold" style={{ fontSize: "16px" }}>
                        <div className="col-12">Status History</div>
                    </div>
                    {statusLogs.length ? (
                        <>
                            <div className="row mb-2">
                                <div className="col-12">
                                    <div className="p-2 border rounded">
                                        <div className="row">
                                            <div className="col-3">Date & Time</div>
                                            <div className="col-3">Country</div>
                                            <div className="col-6">Status</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {statusLogs.map((statusLog: any) => {
                                const { country, ParcelStatus, created_at } = statusLog
                                const { name: status } = ParcelStatus ?? {}
                                const createdAtMoment = moment(created_at ?? "")
                                let dateAndTime = "-,-"
                                dateAndTime = createdAtMoment.isValid()
                                    ? dateFormatter(created_at, true, country)
                                    : dateAndTime
                                let date = dateAndTime.split(",")[0]?.trim()
                                let time = dateAndTime.split(",")[1]?.trim()
                                const statusIndicatorClassName =
                                    status === "Shipment handed over to last mile" ? "bg-success" : "bg-warning"
                                return (
                                    <div key={`${date}-${time}`} className="row mb-2">
                                        <div className="col-12">
                                            <div className="bg-white p-2">
                                                <div className="row">
                                                    <div className="col-3 d-flex flex-column flex-nowrap justify-content-between">
                                                        <div>{date}</div>
                                                        <small className="text-muted">
                                                            {time}&nbsp;-&nbsp;{country}&nbsp;time
                                                        </small>
                                                    </div>
                                                    <div className="col-3 d-flex align-items-center">
                                                        <OverlayTrigger
                                                            placement="bottom"
                                                            delay={{ show: 250, hide: 400 }}
                                                            overlay={
                                                                <Tooltip id={`mawb-${country}-tooltip`}>
                                                                    {countryNamesInEnglish.of(country)}
                                                                </Tooltip>
                                                            }>
                                                            <span className="tr-pointer">{country}&nbsp;</span>
                                                        </OverlayTrigger>
                                                    </div>
                                                    <div className="col-6 d-flex align-items-center">
                                                        <strong
                                                            className={`px-2 text-white ${statusIndicatorClassName} rounded font-13`}>
                                                            {t(status)}
                                                        </strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </>
                    ) : (
                        <div className="row mb-4 text-muted">
                            <div className="col-12">No updates...</div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default memo(LineHaulMAWBSTableExpandedRow)
